/**
 * RocketScene - Rocket launch mini-game
 */

import { useState, useEffect } from 'react';

export default function RocketScene({ level, isComplete, isListening }) {
  const [launched, setLaunched] = useState(false);

  useEffect(() => {
    setLaunched(false);
  }, [level?.id]);

  useEffect(() => {
    if (isComplete) {
      setLaunched(true);
    }
  }, [isComplete]);

  return (
    <div style={styles.scene}>
      {/* Space background */}
      <div style={styles.space}>
        {/* Stars */}
        {[...Array(30)].map((_, i) => (
          <div
            key={i}
            style={{
              ...styles.star,
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
              animationDelay: `${Math.random() * 2}s`,
            }}
          />
        ))}
      </div>

      {/* Ground */}
      <div style={styles.ground}>
        <div style={styles.launchPad} />
      </div>

      {/* Rocket */}
      <div
        style={{
          ...styles.rocket,
          transform: launched 
            ? 'translateX(-50%) translateY(-150vh)' 
            : 'translateX(-50%) translateY(0)',
          transition: launched ? 'transform 2s ease-in' : 'none',
        }}
      >
        <div style={styles.rocketBody}>🚀</div>
        {/* Flame */}
        {(isListening || launched) && (
          <div style={styles.flame}>
            <span style={styles.flameInner}>🔥</span>
          </div>
        )}
      </div>

      {/* Countdown/Status */}
      <div style={styles.status}>
        {launched ? 'BLAST OFF!' : isListening ? 'Say "GO UP"!' : 'Ready to launch'}
      </div>
    </div>
  );
}

const styles = {
  scene: { position: 'absolute', inset: 0, overflow: 'hidden' },
  space: {
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(180deg, #0f172a 0%, #1e3a5f 50%, #38bdf8 100%)',
  },
  star: {
    position: 'absolute',
    width: '4px',
    height: '4px',
    background: 'white',
    borderRadius: '50%',
    animation: 'twinkle 2s ease-in-out infinite',
  },
  ground: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '20%',
    background: 'linear-gradient(180deg, #64748b 0%, #475569 100%)',
  },
  launchPad: {
    position: 'absolute',
    bottom: '20%',
    left: '50%',
    transform: 'translateX(-50%)',
    width: '80px',
    height: '10px',
    background: '#1e293b',
    borderRadius: '2px',
  },
  rocket: {
    position: 'absolute',
    bottom: '25%',
    left: '50%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  rocketBody: {
    fontSize: '80px',
    filter: 'drop-shadow(0 10px 30px rgba(0,0,0,0.5))',
  },
  flame: {
    marginTop: '-20px',
    animation: 'flameFlicker 0.1s ease-in-out infinite',
  },
  flameInner: {
    fontSize: '40px',
  },
  status: {
    position: 'absolute',
    top: '10%',
    left: '50%',
    transform: 'translateX(-50%)',
    background: 'rgba(0,0,0,0.6)',
    color: 'white',
    padding: '12px 24px',
    borderRadius: '30px',
    fontFamily: "'Fredoka', sans-serif",
    fontSize: '18px',
    fontWeight: 600,
  },
};

// Add keyframes
const styleSheet = document.createElement('style');
styleSheet.textContent = `
  @keyframes twinkle {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 1; }
  }
  @keyframes flameFlicker {
    0%, 100% { transform: scaleY(1); }
    50% { transform: scaleY(1.2); }
  }
`;
if (!document.querySelector('#rocket-scene-styles')) {
  styleSheet.id = 'rocket-scene-styles';
  document.head.appendChild(styleSheet);
}
